import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, CircularProgress, IconButton, Tooltip, Avatar, alpha } from '@mui/material';
import {
  People as PeopleIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  FavoriteBorder as FavoriteIcon,
  Refresh as RefreshIcon,
  AddRounded as AddIcon,
  EmojiEvents as TrophyIcon,
  ChatBubbleOutline as ReplyIcon,
  RepeatRounded as RepostIcon,
  ArrowForward as ArrowIcon,
  OpenInNew as OpenInNewIcon,
  CalendarToday as CalendarIcon,
  NorthEast as UpIcon,
  SouthEast as DownIcon,
  PushPin as PinIcon,
  BoltRounded as BoltIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
} from 'recharts';
import { designTokens as t } from '../theme';
import { getToken } from '../api';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// ── helpers ──────────────────────────────────────────────────────────────────
const metaOf = (s) => (s && s.Data && s.Data.meta) || {};
const field = (o, ...keys) => { for (const k of keys) if (o[k] != null) return o[k]; return undefined; };

// 1234 -> "1.2k", 1200000 -> "1.2M"
const fmtCompact = (n) => {
  const x = Number(n) || 0;
  if (Math.abs(x) >= 1e6) return (x / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
  if (Math.abs(x) >= 1e3) return (x / 1e3).toFixed(1).replace(/\.0$/, '') + 'k';
  return String(x);
};
const fmtSigned = (n) => (n > 0 ? '+' : n < 0 ? '−' : '') + fmtCompact(Math.abs(n));

// countdown court : "58m", "2h19", "1j 4h"
const fmtCountdown = (dt) => {
  let s = Math.max(0, Math.round((dt - Date.now()) / 1000));
  const d = Math.floor(s / 86400); s -= d * 86400;
  const h = Math.floor(s / 3600); s -= h * 3600;
  const m = Math.max(1, Math.round(s / 60));
  if (d > 0) return `${d}j ${h}h`;
  if (h > 0) return `${h}h${String(m).padStart(2, '0')}`;
  return `${m}m`;
};
// "aujourd'hui · 14:30" / "demain · 09:15" / "sam. 12 juil. · 08:00"
const fmtWhen = (dt) => {
  const now = new Date();
  const time = dt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  if (dt.toDateString() === now.toDateString()) return `aujourd'hui · ${time}`;
  const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
  if (dt.toDateString() === tomorrow.toDateString()) return `demain · ${time}`;
  return `${dt.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short' })} · ${time}`;
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 6) return 'Good night';
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

const nameFromEmail = (email) => {
  const local = (email || '').split('@')[0].split(/[._+-]/)[0];
  if (!local) return 'there';
  return local.charAt(0).toUpperCase() + local.slice(1);
};

// Construit une série agrégée des followers sur les 7 derniers jours à partir
// des historiques par compte (lecture DB — aucune requête proxy).
const buildFollowerSeries = (histories) => {
  const DAY = 86400000;
  // normalise chaque historique en [{ tt: ms, v }] trié
  const norm = histories.map((hist) =>
    (hist || []).map((h) => {
      let raw = h.date ?? h.timestamp;
      if (typeof raw === 'string' && /^\d+$/.test(raw)) raw = parseInt(raw, 10);
      if (typeof raw === 'number') raw = raw < 1e12 ? raw * 1000 : raw;
      const d = new Date(raw);
      return { tt: d.getTime(), v: Number(h.followers ?? h.count) || 0 };
    }).filter((p) => !isNaN(p.tt)).sort((a, b) => a.tt - b.tt)
  );

  const today = new Date(); today.setHours(23, 59, 59, 999);
  const todayEnd = today.getTime();
  const points = [];
  for (let i = 6; i >= 0; i--) {
    const dayEnd = todayEnd - i * DAY;
    let sum = 0, any = false;
    for (const h of norm) {
      let val = null;
      for (const p of h) { if (p.tt <= dayEnd) val = p.v; else break; }
      if (val != null) { sum += val; any = true; }
    }
    points.push({
      label: new Date(dayEnd).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
      total: any ? sum : null,
    });
  }
  const withData = points.filter((p) => p.total != null);
  const delta = withData.length >= 2 ? withData[withData.length - 1].total - withData[0].total : 0;
  return { points, hasData: withData.length >= 2, delta };
};

// ── primitives ───────────────────────────────────────────────────────────────
const Card = ({ children, sx, onClick }) => (
  <Box onClick={onClick} sx={{ border: `1px solid ${t.border}`, borderRadius: 2.5, bgcolor: t.card, ...sx }}>{children}</Box>
);

const KpiCard = ({ icon, tint, label, value, sub, onClick }) => (
  <Card
    onClick={onClick}
    sx={{
      flex: '1 1 220px', minWidth: 200, p: 2.25, display: 'flex', flexDirection: 'column', gap: 1.25,
      cursor: onClick ? 'pointer' : 'default', transition: 'border-color .15s ease, transform .15s ease',
      '&:hover': onClick ? { borderColor: alpha(tint, 0.55), transform: 'translateY(-2px)' } : {},
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: t.mutedFaint }}>{label}</Typography>
      <Box sx={{ width: 32, height: 32, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(tint, 0.14), color: tint }}>{icon}</Box>
    </Box>
    <Typography sx={{ fontSize: '2.1rem', fontWeight: 700, lineHeight: 1, letterSpacing: '-0.02em' }}>{value}</Typography>
    {sub && <Box sx={{ fontSize: '0.76rem', color: t.muted, display: 'flex', alignItems: 'center', gap: 0.75 }}>{sub}</Box>}
  </Card>
);

const SectionLabel = ({ icon, children, action }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85, color: t.mutedFaint }}>
      {icon}
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{children}</Typography>
    </Box>
    {action}
  </Box>
);

// lien "Voir tout →" en action de section
const SeeAll = ({ onClick, children = 'Voir tout' }) => (
  <Box onClick={onClick} sx={{
    display: 'inline-flex', alignItems: 'center', gap: 0.4, cursor: 'pointer', color: t.primary,
    fontSize: '0.76rem', fontWeight: 600, '&:hover': { color: t.primaryHover },
  }}>
    {children} <ArrowIcon sx={{ fontSize: 14 }} />
  </Box>
);

const Empty = ({ children }) => (
  <Box sx={{ flex: 1, minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', px: 2 }}>
    <Typography sx={{ fontSize: '0.85rem', color: t.mutedFaint }}>{children || 'No data yet'}</Typography>
  </Box>
);

// ── page ─────────────────────────────────────────────────────────────────────
function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [posts, setPosts] = useState([]);
  const [series, setSeries] = useState({ points: [], hasData: false, delta: 0 });
  const [allTimeAccounts, setAllTimeAccounts] = useState(null);
  const email = (typeof localStorage !== 'undefined' && localStorage.getItem('userEmail')) || '';

  const loadCore = useCallback(async () => {
    try {
      const [sRes, pRes, aRes] = await Promise.all([
        axios.get(`${API}/api/sessions`),
        axios.get(`${API}/api/scheduled-posts`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/analytics`).catch(() => ({ data: null })),
      ]);
      setSessions(sRes.data || []);
      setPosts(Array.isArray(pRes.data) ? pRes.data : (pRes.data?.posts || []));
      if (aRes.data && aRes.data.all_time_accounts != null) setAllTimeAccounts(aRes.data.all_time_accounts);
    } catch (e) {
      console.error('Dashboard core load error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Historique followers agrégé (lecture DB par compte, pas de proxy) — chargé
  // une fois au montage + au refresh manuel (pas dans le poll 30s).
  const loadHistory = useCallback(async (sess) => {
    if (!sess || sess.length === 0) { setSeries({ points: [], hasData: false, delta: 0 }); return; }
    const results = await Promise.allSettled(
      sess.map((s) => axios.get(`${API}/api/sessions/${encodeURIComponent(s.Name)}/followers-history`))
    );
    const histories = results.map((r) => (r.status === 'fulfilled' ? (r.value.data?.history || []) : []));
    setSeries(buildFollowerSeries(histories));
  }, []);

  const refresh = useCallback(async () => {
    await loadCore();
    try {
      const { data } = await axios.get(`${API}/api/sessions`);
      await loadHistory(data || []);
    } catch { /* ignore */ }
  }, [loadCore, loadHistory]);

  useEffect(() => {
    let alive = true;
    (async () => {
      await loadCore();
      try {
        const { data } = await axios.get(`${API}/api/sessions`);
        if (alive) await loadHistory(data || []);
      } catch { /* ignore */ }
    })();
    const id = setInterval(loadCore, 30000); // poll léger des compteurs
    return () => { alive = false; clearInterval(id); };
  }, [loadCore, loadHistory]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // ── dérivés ──
  const statusOf = (p) => {
    if (field(p, 'error', 'Error')) return 'failed';
    if (field(p, 'published', 'Published')) return 'published';
    return 'scheduled';
  };
  const publishedCount = posts.filter((p) => statusOf(p) === 'published').length;
  const scheduledCount = posts.filter((p) => statusOf(p) === 'scheduled').length;
  const accountsCount = sessions.length;
  const withProxy = sessions.filter((s) => s.ProxyURL).length;
  const followersTotal = sessions.reduce((acc, s) => acc + (Number(metaOf(s).followers) || 0), 0);

  // index sessions par nom → handle + avatar (photo servie par notre backend,
  // évite le blocage hotlink du CDN IG)
  const sessByName = {};
  sessions.forEach((s) => { sessByName[s.Name] = s; });
  const handleOf = (name) => { const s = sessByName[name]; return s ? (metaOf(s).username || s.Name) : name; };
  const avatarOf = (name) => {
    const s = sessByName[name]; if (!s) return undefined;
    const m = metaOf(s);
    return (m.profile_pic_url || m.profile_pic)
      ? `${API}/api/sessions/${encodeURIComponent(s.Name)}/avatar?token=${getToken()}`
      : undefined;
  };

  // 5 prochains posts programmés (les plus proches dans le temps)
  const now = new Date();
  const nextPosts = posts
    .filter((p) => statusOf(p) === 'scheduled')
    .map((p) => ({ p, when: new Date(field(p, 'scheduledTime', 'ScheduledTime')) }))
    .filter(({ when }) => !isNaN(when) && when > now)
    .sort((a, b) => a.when - b.when)
    .slice(0, 5);

  // top 5 comptes par followers
  const topAccounts = [...sessions]
    .filter((s) => Number(metaOf(s).followers) > 0)
    .sort((a, b) => (Number(metaOf(b).followers) || 0) - (Number(metaOf(a).followers) || 0))
    .slice(0, 5);

  // top 5 des meilleurs posts sur 7 jours (par likes) parmi les stats stockées
  // (meta.post_stats), remplies par le job quotidien (~25s après les abonnés) +
  // le bouton "Stats des posts".
  const nowSec = Date.now() / 1000;
  const SEVEN_DAYS = 7 * 86400;
  const allPosts = [];
  sessions.forEach((s) => {
    const m = metaOf(s);
    const ps = Array.isArray(m.post_stats) ? m.post_stats : [];
    const uname = m.username || s.Name;
    ps.forEach((p) => {
      const taken = Number(p.takenAt) || 0;
      if (taken && nowSec - taken > SEVEN_DAYS) return; // hors fenêtre 7 jours
      allPosts.push({
        sessionName: s.Name, username: uname, code: p.code, text: p.text,
        likes: Number(p.likes) || 0, replies: Number(p.replies) || 0,
        reposts: Number(p.reposts) || 0, takenAt: taken,
      });
    });
  });
  const bestPosts = allPosts.sort((a, b) => b.likes - a.likes).slice(0, 5);
  const postUrl = (p) => (p && p.code && p.username
    ? `https://www.threads.com/@${p.username}/post/${p.code}`
    : (p && p.username ? `https://www.threads.com/@${p.username}` : null));

  const weekDelta = series.delta;
  const rankColor = (i) => (i === 0 ? '#f59e0b' : i === 1 ? '#9ca3af' : i === 2 ? '#b45309' : t.mutedFaint);
  const rowStyle = (i) => ({
    display: 'flex', alignItems: 'center', gap: 1.25, py: 1.1, px: 0.75, mx: -0.75, borderRadius: 1.5,
    borderTop: i === 0 ? 'none' : `1px solid ${t.border}`,
    transition: 'background-color .12s ease', '&:hover': { bgcolor: alpha('#fff', 0.03) },
  });

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1500, mx: 'auto' }}>
      {/* ── header / greeting ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: t.mutedFaint }}>
            {greeting()}
          </Typography>
          <Typography sx={{ fontSize: '1.9rem', fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.15, mt: 0.25 }}>
            {nameFromEmail(email)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, color: t.muted }}>
            <CalendarIcon sx={{ fontSize: 14 }} />
            <Typography sx={{ fontSize: '0.82rem' }}>
              {scheduledCount} post{scheduledCount > 1 ? 's' : ''} programmé{scheduledCount > 1 ? 's' : ''}
              {nextPosts.length > 0 && <> · prochain <b style={{ color: t.foreground }}>dans {fmtCountdown(nextPosts[0].when)}</b></>}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Rafraîchir">
            <IconButton onClick={refresh} sx={{ border: `1px solid ${t.border}`, bgcolor: t.card }}>
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Box
            onClick={() => navigate('/post-creator')}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.85, cursor: 'pointer', userSelect: 'none',
              px: 1.85, py: 1, borderRadius: 2, bgcolor: t.primary, color: '#fff', fontWeight: 600, fontSize: '0.88rem',
              transition: 'background-color .15s ease', '&:hover': { bgcolor: t.primaryHover },
            }}
          >
            <AddIcon sx={{ fontSize: 19 }} /> New Post
          </Box>
        </Box>
      </Box>

      {/* ── KPI row (cliquables) ── */}
      <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', mb: 2.5 }}>
        <KpiCard
          icon={<PeopleIcon sx={{ fontSize: 18 }} />} tint={t.primary} label="Accounts" value={accountsCount}
          sub={<span>{withProxy}/{accountsCount} avec proxy</span>} onClick={() => navigate('/sessions')}
        />
        <KpiCard
          icon={<HistoryIcon sx={{ fontSize: 18 }} />} tint="#a78bfa" label="All time"
          value={fmtCompact(allTimeAccounts != null ? allTimeAccounts : accountsCount)}
          sub={<span>comptes connectés depuis le début</span>}
        />
        <KpiCard
          icon={<SendIcon sx={{ fontSize: 17 }} />} tint={t.success} label="Published" value={fmtCompact(publishedCount)}
          sub={<span>all time</span>} onClick={() => navigate('/scheduled-posts')}
        />
        <KpiCard
          icon={<ScheduleIcon sx={{ fontSize: 18 }} />} tint={t.info} label="Scheduled" value={fmtCompact(scheduledCount)}
          sub={<span>à venir</span>} onClick={() => navigate('/scheduled-posts')}
        />
        <KpiCard
          icon={<FavoriteIcon sx={{ fontSize: 17 }} />} tint={t.error} label="Followers" value={fmtCompact(followersTotal)}
          onClick={() => navigate('/sessions')}
          sub={
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <span>total</span>
              {series.hasData && weekDelta !== 0 && (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color: weekDelta > 0 ? t.success : t.error, fontWeight: 600 }}>
                  {weekDelta > 0 ? <UpIcon sx={{ fontSize: 13 }} /> : <DownIcon sx={{ fontSize: 13 }} />}
                  {fmtSigned(weekDelta)} cette semaine
                </Box>
              )}
            </Box>
          }
        />
      </Box>

      {/* ── row 1 : chart + next posts ── */}
      <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', alignItems: 'stretch', mb: 2.5 }}>
        {/* chart */}
        <Card sx={{ flex: '2 1 520px', minWidth: 320, p: 2.25, display: 'flex', flexDirection: 'column' }}>
          <SectionLabel
            icon={<PeopleIcon sx={{ fontSize: 15 }} />}
            action={<SeeAll onClick={() => navigate('/sessions')}>Voir les comptes</SeeAll>}
          >
            Followers · 7 derniers jours
          </SectionLabel>
          {series.hasData ? (
            <Box sx={{ flex: 1, minHeight: 280, mt: 0.5 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={series.points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="follFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={t.primary} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={t.primary} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={t.border} vertical={false} />
                  <XAxis dataKey="label" stroke={t.mutedFaint} fontSize={11} tickLine={false} axisLine={{ stroke: t.border }} />
                  <YAxis stroke={t.mutedFaint} fontSize={11} tickLine={false} axisLine={false} width={48} tickFormatter={fmtCompact} />
                  <RechartsTooltip
                    contentStyle={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: t.muted }}
                    formatter={(v) => [fmtCompact(v) + ' abonnés', 'Total']}
                  />
                  <Area type="monotone" dataKey="total" stroke={t.primary} strokeWidth={2} fill="url(#follFill)" connectNulls dot={{ r: 2.5, fill: t.primary }} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <Empty>Pas encore assez d'historique — reviens après quelques relevés quotidiens.</Empty>
          )}
        </Card>

        {/* next posts */}
        <Card sx={{ flex: '1 1 340px', minWidth: 300, p: 2.25, display: 'flex', flexDirection: 'column' }}>
          <SectionLabel
            icon={<BoltIcon sx={{ fontSize: 16 }} />}
            action={<SeeAll onClick={() => navigate('/scheduled-posts')} />}
          >
            Next posts
          </SectionLabel>
          {nextPosts.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {nextPosts.map(({ p, when }, i) => {
                const acc = field(p, 'session', 'Session');
                const msg = field(p, 'message', 'Message') || '';
                const isSticky = !!field(p, 'sticky', 'Sticky');
                const soon = when - now < 3600000; // < 1h
                return (
                  <Box key={field(p, 'id', 'ID') || i} sx={rowStyle(i)}>
                    <Avatar src={avatarOf(acc)} sx={{ width: 32, height: 32, flexShrink: 0, fontSize: '0.82rem', bgcolor: alpha(t.primary, 0.18), color: '#c7d2fe', fontWeight: 700 }}>
                      {(handleOf(acc) || '?').charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          @{handleOf(acc)}
                        </Typography>
                        {isSticky && <PinIcon sx={{ fontSize: 12, color: '#ec4899', flexShrink: 0 }} />}
                      </Box>
                      <Typography sx={{ fontSize: '0.76rem', color: t.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {msg || '(image / média)'}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                      <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, color: soon ? '#f59e0b' : t.foreground }}>
                        dans {fmtCountdown(when)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: t.mutedFaint }}>{fmtWhen(when)}</Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Empty>Aucun post programmé — clique "New Post" pour lancer une programmation.</Empty>
          )}
        </Card>
      </Box>

      {/* ── row 2 : best posts + top accounts ── */}
      <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', alignItems: 'stretch' }}>
        {/* best posts (top 5) */}
        <Card sx={{ flex: '2 1 520px', minWidth: 320, p: 2.25, display: 'flex', flexDirection: 'column' }}>
          <SectionLabel icon={<TrophyIcon sx={{ fontSize: 15 }} />}>Best posts · 7 jours</SectionLabel>
          {bestPosts.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {bestPosts.map((p, i) => {
                const url = postUrl(p);
                return (
                  <Box key={`${p.username}-${p.code || i}`} sx={rowStyle(i)}>
                    <Typography sx={{ width: 20, textAlign: 'center', flexShrink: 0, fontSize: '0.9rem', fontWeight: 700, color: rankColor(i) }}>
                      {i + 1}
                    </Typography>
                    <Avatar src={avatarOf(p.sessionName)} sx={{ width: 32, height: 32, flexShrink: 0, fontSize: '0.82rem', bgcolor: alpha(t.primary, 0.18), color: '#c7d2fe', fontWeight: 700 }}>
                      {(p.username || '?').charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        @{p.username}
                      </Typography>
                      <Typography sx={{ fontSize: '0.78rem', color: t.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {p.text || '— (post image / sans texte)'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.25, sm: 2 }, flexShrink: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <FavoriteIcon sx={{ fontSize: 15, color: '#f43f5e' }} />
                        <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, minWidth: 24 }}>{fmtCompact(p.likes)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: t.muted }}>
                        <ReplyIcon sx={{ fontSize: 14 }} />
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, minWidth: 20 }}>{fmtCompact(p.replies)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: t.muted }}>
                        <RepostIcon sx={{ fontSize: 14 }} />
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, minWidth: 20 }}>{fmtCompact(p.reposts)}</Typography>
                      </Box>
                      {url && (
                        <Tooltip title="Ouvrir sur Threads">
                          <IconButton size="small" component="a" href={url} target="_blank" rel="noopener noreferrer" sx={{ color: t.muted, p: 0.4 }}>
                            <OpenInNewIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : <Empty>Pas encore de stats — clique "Stats des posts" sur un compte, ou attends le relevé quotidien.</Empty>}
        </Card>

        {/* top accounts (top 5 followers) */}
        <Card sx={{ flex: '1 1 340px', minWidth: 300, p: 2.25, display: 'flex', flexDirection: 'column' }}>
          <SectionLabel
            icon={<TrophyIcon sx={{ fontSize: 15 }} />}
            action={<SeeAll onClick={() => navigate('/sessions')} />}
          >
            Top accounts · followers
          </SectionLabel>
          {topAccounts.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {topAccounts.map((s, i) => {
                const m = metaOf(s);
                const handle = m.username || s.Name;
                return (
                  <Box key={s.Name} sx={rowStyle(i)}>
                    <Typography sx={{ width: 20, textAlign: 'center', flexShrink: 0, fontSize: '0.9rem', fontWeight: 700, color: rankColor(i) }}>
                      {i + 1}
                    </Typography>
                    <Avatar src={avatarOf(s.Name)} sx={{ width: 32, height: 32, flexShrink: 0, fontSize: '0.82rem', bgcolor: alpha(t.primary, 0.18), color: '#c7d2fe', fontWeight: 700 }}>
                      {handle.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography sx={{ flex: 1, minWidth: 0, fontSize: '0.82rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      @{handle}
                    </Typography>
                    <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, flexShrink: 0 }}>
                      {fmtCompact(m.followers)}
                      <Box component="span" sx={{ fontSize: '0.7rem', fontWeight: 500, color: t.mutedFaint, ml: 0.4 }}>abonnés</Box>
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ) : <Empty>Pas encore de relevé d'abonnés.</Empty>}
        </Card>
      </Box>
    </Box>
  );
}

export default Dashboard;
